import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './Lineage.css';

const Lineage = () => {
  usePageTitle('Data Lineage');
  const [flows, setFlows] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [lineageEntries, setLineageEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [viewMode, setViewMode] = useState('graph'); // 'graph' or 'table'
  const [layerFilter, setLayerFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [flowsRes, lineageRes] = await Promise.all([
        axiosInstance.get('/etl/flows/'),
        axiosInstance.get('/etl/lineage/'),
      ]);
      setFlows(flowsRes.data.results || flowsRes.data);

      // Build dependencies from lineage data
      const lineageData = lineageRes.data.results || lineageRes.data;
      setLineageEntries(lineageData);

      // Also fetch dependencies for each flow
      const allDeps = [];
      for (const flow of (flowsRes.data.results || flowsRes.data)) {
        try {
          const depsRes = await axiosInstance.get(`/etl/flows/${flow.id}/dependencies/`);
          if (depsRes.data.depends_on) {
            depsRes.data.depends_on.forEach(dep => {
              allDeps.push({
                from_flow_id: dep.id,
                from_flow_name: dep.name,
                to_flow_id: flow.id,
                to_flow_name: flow.name,
                dependency_type: dep.dependency_type,
              });
            });
          }
        } catch (err) {
          // Ignore individual flow errors
        }
      }
      setDependencies(allDeps);
    } catch (err) {
      console.error('Failed to fetch lineage data:', err);
      setError('Failed to load lineage data');
    } finally {
      setLoading(false);
    }
  };

  // Build graph data structure
  const graphData = useMemo(() => {
    const nodes = new Map();
    const edges = [];

    // Add all flows as nodes
    flows.forEach(flow => {
      const layer = flow.bq_dataset === 'stg' ? 'bronze' :
                   flow.bq_dataset === 'int' ? 'silver' :
                   flow.bq_dataset === 'analytics' ? 'gold' : 'other';

      if (layerFilter !== 'all' && layer !== layerFilter) return;
      if (searchTerm && !flow.name.toLowerCase().includes(searchTerm.toLowerCase())) return;

      nodes.set(flow.id, {
        id: flow.id,
        name: flow.name,
        layer,
        flowType: flow.flow_type || 'odoo',
        status: flow.status,
        table: flow.bq_table_name,
      });
    });

    // Add dependency edges
    dependencies.forEach(dep => {
      if (nodes.has(dep.from_flow_id) && nodes.has(dep.to_flow_id)) {
        edges.push({
          from: dep.from_flow_id,
          to: dep.to_flow_id,
          type: dep.dependency_type,
        });
      }
    });

    // Add lineage edges (SQL source tables)
    lineageEntries.forEach(entry => {
      if (entry.source_flow && nodes.has(entry.source_flow) && nodes.has(entry.flow)) {
        // Check if edge already exists
        const exists = edges.some(e => e.from === entry.source_flow && e.to === entry.flow);
        if (!exists) {
          edges.push({
            from: entry.source_flow,
            to: entry.flow,
            type: entry.lineage_type,
          });
        }
      }
    });

    return { nodes: Array.from(nodes.values()), edges };
  }, [flows, dependencies, lineageEntries, layerFilter, searchTerm]);

  // Get upstream and downstream flows for selected flow
  const getFlowLineage = useCallback((flowId) => {
    const upstream = [];
    const downstream = [];
    const visited = new Set();

    // Find upstream (flows that this flow depends on)
    const findUpstream = (id, depth = 0) => {
      if (depth > 10 || visited.has(`up-${id}`)) return;
      visited.add(`up-${id}`);

      graphData.edges.forEach(edge => {
        if (edge.to === id) {
          const node = graphData.nodes.find(n => n.id === edge.from);
          if (node) {
            upstream.push({ ...node, depth, edgeType: edge.type });
            findUpstream(edge.from, depth + 1);
          }
        }
      });
    };

    // Find downstream (flows that depend on this flow)
    const findDownstream = (id, depth = 0) => {
      if (depth > 10 || visited.has(`down-${id}`)) return;
      visited.add(`down-${id}`);

      graphData.edges.forEach(edge => {
        if (edge.from === id) {
          const node = graphData.nodes.find(n => n.id === edge.to);
          if (node) {
            downstream.push({ ...node, depth, edgeType: edge.type });
            findDownstream(edge.to, depth + 1);
          }
        }
      });
    };

    findUpstream(flowId);
    findDownstream(flowId);

    return { upstream, downstream };
  }, [graphData]);

  const selectedFlowLineage = selectedFlow ? getFlowLineage(selectedFlow.id) : null;

  // Group nodes by layer for visual layout
  const layerGroups = useMemo(() => {
    const groups = { bronze: [], silver: [], gold: [], other: [] };
    graphData.nodes.forEach(node => {
      groups[node.layer]?.push(node) || groups.other.push(node);
    });
    return groups;
  }, [graphData.nodes]);

  const getLayerBadgeClass = (layer) => {
    switch (layer) {
      case 'bronze': return 'layer-badge layer-bronze';
      case 'silver': return 'layer-badge layer-silver';
      case 'gold': return 'layer-badge layer-gold';
      default: return 'layer-badge';
    }
  };

  const getFlowTypeBadgeClass = (flowType) => {
    switch (flowType) {
      case 'odoo': return 'flow-type-badge flow-type-odoo';
      case 'sql': return 'flow-type-badge flow-type-sql';
      default: return 'flow-type-badge';
    }
  };

  if (loading) {
    return (
      <div className="lineage-container">
        <div className="loading-state">Loading lineage data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lineage-container">
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="lineage-container">
      <div className="lineage-header">
        <div className="header-title">
          <h1>Data Lineage</h1>
          <p className="header-subtitle">
            Visualize dependencies and data flow between ETL flows
          </p>
        </div>
        <div className="header-stats">
          <div className="stat">
            <span className="stat-value">{graphData.nodes.length}</span>
            <span className="stat-label">Flows</span>
          </div>
          <div className="stat">
            <span className="stat-value">{graphData.edges.length}</span>
            <span className="stat-label">Dependencies</span>
          </div>
        </div>
      </div>

      <div className="lineage-controls">
        <input
          type="text"
          placeholder="Search flows..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="filter-chips">
          <button
            className={`filter-chip ${layerFilter === 'all' ? 'active' : ''}`}
            onClick={() => setLayerFilter('all')}
          >
            All Layers
          </button>
          <button
            className={`filter-chip filter-bronze ${layerFilter === 'bronze' ? 'active' : ''}`}
            onClick={() => setLayerFilter('bronze')}
          >
            Bronze
          </button>
          <button
            className={`filter-chip filter-silver ${layerFilter === 'silver' ? 'active' : ''}`}
            onClick={() => setLayerFilter('silver')}
          >
            Silver
          </button>
          <button
            className={`filter-chip filter-gold ${layerFilter === 'gold' ? 'active' : ''}`}
            onClick={() => setLayerFilter('gold')}
          >
            Gold
          </button>
        </div>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'graph' ? 'active' : ''}`}
            onClick={() => setViewMode('graph')}
          >
            Graph View
          </button>
          <button
            className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            Table View
          </button>
        </div>
      </div>

      <div className="lineage-content">
        {viewMode === 'graph' ? (
          <div className="graph-view">
            <div className="layers-container">
              {/* Bronze Layer */}
              <div className="layer-column">
                <div className="layer-header bronze">
                  <span className="layer-icon">🥉</span>
                  <span>Bronze (stg)</span>
                  <span className="layer-count">{layerGroups.bronze.length}</span>
                </div>
                <div className="layer-nodes">
                  {layerGroups.bronze.map(node => (
                    <div
                      key={node.id}
                      className={`node-card ${selectedFlow?.id === node.id ? 'selected' : ''}`}
                      onClick={() => setSelectedFlow(selectedFlow?.id === node.id ? null : node)}
                    >
                      <div className="node-name">{node.name}</div>
                      <div className="node-meta">
                        <span className={getFlowTypeBadgeClass(node.flowType)}>
                          {node.flowType}
                        </span>
                        <span className="node-table">{node.table}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Arrow */}
              <div className="layer-arrow">
                <svg viewBox="0 0 24 24" width="32" height="32">
                  <path d="M8 4l8 8-8 8" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              {/* Silver Layer */}
              <div className="layer-column">
                <div className="layer-header silver">
                  <span className="layer-icon">🥈</span>
                  <span>Silver (int)</span>
                  <span className="layer-count">{layerGroups.silver.length}</span>
                </div>
                <div className="layer-nodes">
                  {layerGroups.silver.map(node => (
                    <div
                      key={node.id}
                      className={`node-card ${selectedFlow?.id === node.id ? 'selected' : ''}`}
                      onClick={() => setSelectedFlow(selectedFlow?.id === node.id ? null : node)}
                    >
                      <div className="node-name">{node.name}</div>
                      <div className="node-meta">
                        <span className={getFlowTypeBadgeClass(node.flowType)}>
                          {node.flowType}
                        </span>
                        <span className="node-table">{node.table}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Arrow */}
              <div className="layer-arrow">
                <svg viewBox="0 0 24 24" width="32" height="32">
                  <path d="M8 4l8 8-8 8" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              {/* Gold Layer */}
              <div className="layer-column">
                <div className="layer-header gold">
                  <span className="layer-icon">🥇</span>
                  <span>Gold (analytics)</span>
                  <span className="layer-count">{layerGroups.gold.length}</span>
                </div>
                <div className="layer-nodes">
                  {layerGroups.gold.map(node => (
                    <div
                      key={node.id}
                      className={`node-card ${selectedFlow?.id === node.id ? 'selected' : ''}`}
                      onClick={() => setSelectedFlow(selectedFlow?.id === node.id ? null : node)}
                    >
                      <div className="node-name">{node.name}</div>
                      <div className="node-meta">
                        <span className={getFlowTypeBadgeClass(node.flowType)}>
                          {node.flowType}
                        </span>
                        <span className="node-table">{node.table}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Selected Flow Details */}
            {selectedFlow && selectedFlowLineage && (
              <div className="flow-details-panel">
                <div className="panel-header">
                  <h3>{selectedFlow.name}</h3>
                  <button className="close-btn" onClick={() => setSelectedFlow(null)}>×</button>
                </div>
                <div className="panel-content">
                  <div className="panel-section">
                    <Link to={`/flows/${selectedFlow.id}`} className="btn-view-flow">
                      View Flow Details →
                    </Link>
                  </div>

                  <div className="panel-section">
                    <h4>Upstream Dependencies ({selectedFlowLineage.upstream.length})</h4>
                    {selectedFlowLineage.upstream.length === 0 ? (
                      <p className="no-deps">No upstream dependencies (source flow)</p>
                    ) : (
                      <ul className="dep-list">
                        {selectedFlowLineage.upstream.map((dep, idx) => (
                          <li key={idx} className="dep-item">
                            <span className={getLayerBadgeClass(dep.layer)}>{dep.layer}</span>
                            <Link to={`/flows/${dep.id}`}>{dep.name}</Link>
                            <span className="dep-type">{dep.edgeType}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="panel-section">
                    <h4>Downstream Dependents ({selectedFlowLineage.downstream.length})</h4>
                    {selectedFlowLineage.downstream.length === 0 ? (
                      <p className="no-deps">No downstream dependents (terminal flow)</p>
                    ) : (
                      <ul className="dep-list">
                        {selectedFlowLineage.downstream.map((dep, idx) => (
                          <li key={idx} className="dep-item">
                            <span className={getLayerBadgeClass(dep.layer)}>{dep.layer}</span>
                            <Link to={`/flows/${dep.id}`}>{dep.name}</Link>
                            <span className="dep-type">{dep.edgeType}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="table-view">
            <table className="lineage-table">
              <thead>
                <tr>
                  <th>Flow</th>
                  <th>Layer</th>
                  <th>Type</th>
                  <th>Table</th>
                  <th>Upstream</th>
                  <th>Downstream</th>
                </tr>
              </thead>
              <tbody>
                {graphData.nodes.map(node => {
                  const lineage = getFlowLineage(node.id);
                  return (
                    <tr key={node.id}>
                      <td>
                        <Link to={`/flows/${node.id}`}>{node.name}</Link>
                      </td>
                      <td>
                        <span className={getLayerBadgeClass(node.layer)}>{node.layer}</span>
                      </td>
                      <td>
                        <span className={getFlowTypeBadgeClass(node.flowType)}>{node.flowType}</span>
                      </td>
                      <td className="table-name">{node.table}</td>
                      <td>
                        {lineage.upstream.length > 0 ? (
                          <span className="dep-count">{lineage.upstream.length} flows</span>
                        ) : (
                          <span className="no-deps-badge">Source</span>
                        )}
                      </td>
                      <td>
                        {lineage.downstream.length > 0 ? (
                          <span className="dep-count">{lineage.downstream.length} flows</span>
                        ) : (
                          <span className="no-deps-badge">Terminal</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="lineage-legend">
        <h4>Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color bronze"></span>
            <span>Bronze - Raw data from Odoo (stg dataset)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color silver"></span>
            <span>Silver - Cleaned/transformed data (int dataset)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color gold"></span>
            <span>Gold - Analytics-ready data (analytics dataset)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lineage;
